/**
 * Markdown → HTML for editor preview, PDF (via html-render), and export parity.
 * Single parser so preview matches PDF/DOCX output structure.
 */

export function escHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Allow http(s), mailto, and relative links; neutralize everything else. */
function safeHref(href: string): string {
  const trimmed = href.trim();
  if (/^(https?:|mailto:)/i.test(trimmed)) return trimmed;
  // Scheme-relative or path/fragment links are fine.
  if (/^(\/\/|\/|#|\.\/|\.\.\/)/.test(trimmed)) return trimmed;
  // A bare relative path with no scheme (e.g. "page.md") is fine.
  if (!/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  return "#";
}

// Inline + table HTML the author may embed (commonly from Notion exports).
// Everything else stays escaped — this is an allowlist, so <script> etc. never
// survive. Table tags are passed through inside raw-HTML table blocks (below).
const ALLOWED_TAGS = new Set([
  "b",
  "i",
  "strong",
  "em",
  "u",
  "s",
  "sub",
  "sup",
  "mark",
  "small",
  "br",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "td",
  "th",
  "caption",
  "colgroup",
  "col",
]);

/** Validate a CSS color value before letting it into an inline style. */
function safeColor(v: string): string | null {
  const t = v.trim();
  if (!t || t.length > 32) return null;
  if (/^#[0-9a-f]{3,8}$/i.test(t)) return t;
  if (/^[a-z]+$/i.test(t)) return t;
  if (/^rgba?\([0-9.,%\s]+\)$/i.test(t)) return t;
  return null;
}

/**
 * Re-enable a small allowlist of inline HTML on already-escaped text:
 * basic formatting tags and `<span color="…">` (mapped to a sanitized style).
 * Unknown/unsafe tags are left escaped.
 */
function applyAllowedHtml(s: string): string {
  return (
    s
      .replace(
        /&lt;span\s+color=&quot;([^&]*?)&quot;\s*&gt;/gi,
        (_m, c: string) => {
          const col = safeColor(c);
          return col ? `<span style="color:${col}">` : "<span>";
        },
      )
      .replace(/&lt;span&gt;/gi, "<span>")
      .replace(/&lt;\/span&gt;/gi, "</span>")
      // Allowlisted tags: either bare/self-closing (`<table>`, `</tr>`, `<br/>`)
      // or carrying real attributes (which contain `=`, e.g. Notion's
      // `<table header-row="true">`). Attributes are dropped — only the tag name
      // survives — so this stays a safe allowlist. Requiring `=` for the
      // attributed form keeps inequality prose like `a<b and c>d` from being
      // mistaken for a `<b>` tag.
      .replace(
        /&lt;(\/?)([a-z][a-z0-9]*)(?:\s*\/?&gt;|\s[\s\S]*?=[\s\S]*?&gt;)/gi,
        (m, slash: string, tag: string) =>
          ALLOWED_TAGS.has(tag.toLowerCase())
            ? `<${slash}${tag.toLowerCase()}>`
            : m,
      )
  );
}

/** Inline markdown: bold, italics, code, links, escaped punctuation, allowed HTML.
 *  Input must be pre-escaped via escHtml. */
export function inlineMarkdown(s: string): string {
  // Protect backslash-escaped punctuation so it renders literally (e.g. `\|`).
  const literals: string[] = [];
  let work = s.replace(/\\([\\`*_{}[\]()#+\-.!|>~])/g, (_m, ch: string) => {
    literals.push(ch);
    return ` ${literals.length - 1} `;
  });

  work = work
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(
      /\[([^\]]+)\]\(([^)\s]+)\)/g,
      (_m, text: string, href: string) =>
        `<a href="${safeHref(href)}">${text}</a>`,
    );

  work = applyAllowedHtml(work);

  // Restore protected literals (re-escaped so they stay inert).
  // eslint-disable-next-line no-control-regex -- NUL bytes are intentional delimiters for the inline-markdown literal placeholder system; they never appear in rendered output
  return work.replace(/ (\d+) /g, (_m, i: string) =>
    escHtml(literals[Number(i)]),
  );
}

function stripTags(s: string): string {
  return s.replace(/<[^>]+>/g, "");
}

/**
 * Notion exports header rows as `<table header-row="true">` with `<td>` cells
 * (no `<th>`). Promote the first row's cells to `<th>` so they pick up the
 * styled (green) table-header treatment shared with markdown pipe tables.
 */
function promoteHeaderRow(tableHtml: string): string {
  const firstTr = tableHtml.match(/<tr[\s\S]*?<\/tr>/i);
  if (!firstTr) return tableHtml;
  const promoted = firstTr[0]
    .replace(/<td>/gi, "<th>")
    .replace(/<\/td>/gi, "</th>");
  return tableHtml.replace(firstTr[0], promoted);
}

function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/&[a-z]+;/g, " ")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "section"
  );
}

const TOC_PLACEHOLDER = " TOC ";

export function markdownToHtml(md: string): string {
  const lines = md.replace(/\n{3,}/g, "\n\n").split("\n");
  const out: string[] = [];
  let inCode = false;
  let codeBuf: string[] = [];
  let inRawTable = false;
  let rawTableBuf: string[] = [];
  let tableBuf: string[][] = [];
  const listStack: Array<{ tag: "ul" | "ol"; indent: number }> = [];
  let firstHeadingDone = false;
  let inTocBlock = false;
  const headings: Array<{ level: number; label: string; slug: string }> = [];
  const usedSlugs = new Set<string>();

  const uniqueSlug = (text: string): string => {
    const base = slugify(text);
    let slug = base;
    let n = 2;
    while (usedSlugs.has(slug)) slug = `${base}-${n++}`;
    usedSlugs.add(slug);
    return slug;
  };

  const closeLists = () => {
    while (listStack.length) out.push(`</${listStack.pop()!.tag}>`);
  };

  // Open/adjust the list stack for an item at the given indent and tag, emitting
  // the needed close/open tags so the caller can push its <li> afterwards.
  // Deeper indents nest; shallower indents close levels. The first top-level
  // list before any section heading is treated as the table of contents.
  const openListLevel = (indent: number, tag: "ul" | "ol", extraCls = "") => {
    while (
      listStack.length &&
      listStack[listStack.length - 1].indent > indent
    ) {
      out.push(`</${listStack.pop()!.tag}>`);
    }
    const top = listStack[listStack.length - 1];
    if (top && top.indent === indent) {
      if (top.tag === tag) return;
      out.push(`</${listStack.pop()!.tag}>`);
    }
    // A leading ordered list (before any section heading) opens the table of
    // contents; following top-level lists in that block share its smaller font.
    if (listStack.length === 0 && !firstHeadingDone && tag === "ol")
      inTocBlock = true;
    const cls = [
      extraCls,
      listStack.length === 0 && inTocBlock && !firstHeadingDone
        ? "toc-list"
        : "",
    ]
      .filter(Boolean)
      .join(" ");
    out.push(cls ? `<${tag} class="${cls}">` : `<${tag}>`);
    listStack.push({ tag, indent });
  };
  const flushTable = () => {
    if (tableBuf.length === 0) return;
    const [head, ...rows] = tableBuf;
    out.push("<table><thead><tr>");
    for (const cell of head) out.push(`<th>${inlineMarkdown(cell)}</th>`);
    out.push("</tr></thead><tbody>");
    for (const row of rows) {
      out.push("<tr>");
      for (const cell of row) out.push(`<td>${inlineMarkdown(cell)}</td>`);
      out.push("</tr>");
    }
    out.push("</tbody></table>");
    tableBuf = [];
  };
  // Flush a buffered raw <table> block, promoting its first row to <th> when
  // the source marked it a header row (Notion's `header-row="true"`).
  const flushRawTable = () => {
    if (rawTableBuf.length === 0) return;
    const raw = rawTableBuf.join("\n");
    const rendered = inlineMarkdown(raw);
    out.push(
      /header-row=&quot;?true/i.test(raw)
        ? promoteHeaderRow(rendered)
        : rendered,
    );
    rawTableBuf = [];
  };

  for (const rawLine of lines) {
    const line = escHtml(rawLine);
    const trimmed = line.trim();

    if (trimmed.startsWith("```")) {
      if (inCode) {
        out.push(`<pre><code>${codeBuf.join("\n")}</code></pre>`);
        codeBuf = [];
        inCode = false;
      } else {
        closeLists();
        flushTable();
        inCode = true;
      }
      continue;
    }
    if (inCode) {
      codeBuf.push(line);
      continue;
    }

    // Raw HTML <table> block (common in Notion exports) — pass through verbatim
    // so rows aren't wrapped in <p>, un-escaping the allowed table tags.
    if (!inRawTable && /^&lt;table(&gt;|\s)/i.test(trimmed)) {
      closeLists();
      flushTable();
      inRawTable = true;
      rawTableBuf = [line];
      continue;
    }
    if (inRawTable) {
      // A missing or merged `</table>` must not let the table swallow the rest
      // of the document. If a line opens a block that can never sit inside a
      // table (heading, horizontal rule, or a new table) and doesn't itself
      // close the table, flush what we have and fall through so this line gets
      // processed normally below instead of being absorbed into the table.
      const closesTable = /&lt;\/table&gt;/i.test(trimmed);
      const bailsOut =
        !closesTable &&
        (/^#{1,6}\s+/.test(trimmed) ||
          /^[-*_]{3,}\s*$/.test(trimmed) ||
          /^&lt;table(&gt;|\s)/i.test(trimmed));
      if (!bailsOut) {
        rawTableBuf.push(line);
        if (closesTable) {
          flushRawTable();
          inRawTable = false;
        }
        continue;
      }
      flushRawTable();
      inRawTable = false;
    }

    // <table_of_contents/> directive — emit a placeholder, fill it in once all
    // headings are known (the directive usually precedes them).
    if (
      trimmed.toLowerCase().startsWith("&lt;table_of_contents") &&
      trimmed.endsWith("&gt;")
    ) {
      closeLists();
      flushTable();
      out.push(TOC_PLACEHOLDER);
      continue;
    }

    if (/^\|.*\|$/.test(trimmed)) {
      closeLists();
      if (!/^\|[\s\-:|]+\|$/.test(trimmed)) {
        tableBuf.push(
          trimmed
            .slice(1, -1)
            .split("|")
            .map((c) => c.trim()),
        );
      }
      continue;
    }
    flushTable();

    if (trimmed === "") {
      closeLists();
      continue;
    }

    if (/^[-*_]{3,}\s*$/.test(trimmed)) {
      closeLists();
      out.push("<hr/>");
      continue;
    }

    const h = trimmed.match(/^(#{1,6})\s+(.+)/);
    if (h) {
      closeLists();
      const lvl = h[1].length;
      const inner = inlineMarkdown(h[2]);
      const label = stripTags(inner);
      const slug = uniqueSlug(label);
      if (lvl >= 2 && !/^table of contents$/i.test(label.trim())) {
        headings.push({ level: lvl, label, slug });
      }
      // Documents open with a table of contents (a leading list or the
      // <table_of_contents/> directive); isolate it on its own page by breaking
      // before the first section heading.
      const breakBefore =
        !firstHeadingDone &&
        out.some((s) => /<li/.test(s) || s.includes(TOC_PLACEHOLDER));
      firstHeadingDone = true;
      inTocBlock = false;
      out.push(
        `<h${lvl} id="${slug}"${breakBefore ? ' style="page-break-before: always"' : ""}>${inner}</h${lvl}>`,
      );
      continue;
    }

    if (trimmed.startsWith("&gt;")) {
      closeLists();
      const quote = trimmed.replace(/^&gt;\s?/, "");
      out.push(`<blockquote>${inlineMarkdown(quote)}</blockquote>`);
      continue;
    }

    const indent = line.length - line.replace(/^ +/, "").length;

    const check = line.match(/^\s*[-*+]\s+\[([xX ]?)\]\s+(.+)/);
    if (check) {
      openListLevel(indent, "ul", "checklist");
      const mark = check[1].toLowerCase() === "x" ? "&#9745;" : "&#9744;";
      out.push(`<li class="check">${mark} ${inlineMarkdown(check[2])}</li>`);
      continue;
    }

    const bullet = line.match(/^\s*[-*+]\s+(.+)/);
    if (bullet) {
      openListLevel(indent, "ul");
      out.push(`<li>${inlineMarkdown(bullet[1])}</li>`);
      continue;
    }

    const num = line.match(/^\s*\d+[.)]\s+(.+)/);
    if (num) {
      openListLevel(indent, "ol");
      out.push(`<li>${inlineMarkdown(num[1])}</li>`);
      continue;
    }

    // An indented plain line inside a list (e.g. a manually-numbered TOC
    // sub-entry like "2.1 Objective") becomes a nested, marker-less item so it
    // renders indented under its parent without breaking the parent numbering.
    if (listStack.length && indent >= 2) {
      openListLevel(indent, "ul", "sub-list");
      out.push(`<li>${inlineMarkdown(trimmed)}</li>`);
      continue;
    }

    const formula = trimmed.match(/^\$\$(.+?)\$\$/);
    if (formula) {
      closeLists();
      out.push(`<div class="formula">${formula[1]}</div>`);
      continue;
    }

    closeLists();
    out.push(`<p>${inlineMarkdown(line)}</p>`);
  }

  if (inCode && codeBuf.length)
    out.push(`<pre><code>${codeBuf.join("\n")}</code></pre>`);
  flushRawTable();
  flushTable();
  closeLists();

  let html = out.join("\n");
  if (html.includes(TOC_PLACEHOLDER)) {
    const toc = headings.length
      ? `<nav class="toc">\n${headings
          .map(
            (hd) =>
              `<div class="toc-item toc-l${hd.level}"><a href="#${hd.slug}">${hd.label}</a></div>`,
          )
          .join("\n")}\n</nav>`
      : "";
    html = html.split(TOC_PLACEHOLDER).join(toc);
  }
  return html;
}
